import { NavLink } from "react-router-dom";
import { useAuth } from "@features/auth/AuthContext";
import { useScheduling } from "@features/patients/queries";
import type { Role } from "@/types/session";

type NavEntry = {
  to: string;
  id: string;
  icon: string;
  label: string;
  roles: Role[];
  badge?: "pendientes";
};

type NavBlock = {
  label: string;
  roles?: Role[];
  items: NavEntry[];
};

const NAV: NavBlock[] = [
  {
    label: "Estratégico",
    roles: ["Administrador", "Auditoria"],
    items: [
      { to: "/dashboard",   id: "nav-dashboard",   icon: "📊", label: "Dashboard",   roles: ["Administrador", "Auditoria", "Evaluador", "Programador"] },
      { to: "/indicadores", id: "nav-indicadores", icon: "📈", label: "Indicadores", roles: ["Administrador", "Auditoria"] },
      { to: "/informes",    id: "nav-informes",    icon: "📑", label: "Informes",    roles: ["Administrador", "Auditoria"] },
    ],
  },
  {
    label: "Gestión Operativa",
    items: [
      { to: "/pendientes", id: "nav-pendientes", icon: "👥", label: "Pacientes pendientes",     roles: ["Administrador", "Evaluador", "Programador", "Auditoria"], badge: "pendientes" },
      { to: "/nueva",      id: "nav-nueva",      icon: "➕", label: "Nueva valoración",         roles: ["Administrador", "Evaluador"] },
      { to: "/historial",  id: "nav-historial",  icon: "📋", label: "Historial de valoraciones", roles: ["Administrador", "Evaluador", "Auditoria", "Programador"] },
      { to: "/carga",      id: "nav-carga",      icon: "📥", label: "Carga Excel",              roles: ["Administrador", "Programador"] },
    ],
  },
  {
    label: "Sistema",
    roles: ["Administrador"],
    items: [
      { to: "/usuarios",  id: "nav-usuarios",  icon: "👤", label: "Usuarios",  roles: ["Administrador"] },
      { to: "/auditoria", id: "nav-auditoria", icon: "🔍", label: "Auditoría", roles: ["Administrador", "Auditoria"] },
    ],
  },
];

type Props = {
  open: boolean;
  onNavigate: () => void;
};

export function Sidebar({ open, onNavigate }: Props) {
  const { user, hasRole } = useAuth();
  const { data: pendData } = useScheduling({ page: 1, limit: 1 });
  const pendCount = pendData?.total ?? 0;

  if (!user) return null;

  return (
    <nav id="sidebar" className={open ? "open" : ""}>
      <div className="sidebar-brand">
        <img src="/images/logo-sidebar.png" alt="FOCA" className="sidebar-logo" />
        <div className="sidebar-brand-text">
          <div className="name">Valoración Preoperatoria</div>
          <div className="sub">Fundación Oftalmológica del Caribe</div>
        </div>
      </div>

      <div className="sidebar-user">
        <div className="user-avatar" id="sidebarAvatar">
          {user.name.charAt(0).toUpperCase()}
        </div>
        <div className="user-info">
          <div className="user-name">{user.name}</div>
          <div className="user-role">{user.role}</div>
        </div>
      </div>

      <div className="sidebar-nav">
        {NAV.map((block) => {
          if (block.roles && !hasRole(...block.roles)) return null;
          const visibleItems = block.items.filter((it) => hasRole(...it.roles));
          if (visibleItems.length === 0) return null;
          return (
            <div key={block.label}>
              <div className="nav-section-label">{block.label}</div>
              {visibleItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onNavigate}
                  className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
                  id={item.id}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span>{item.label}</span>
                  {item.badge === "pendientes" && pendCount > 0 && (
                    <span className="sidebar-badge">{pendCount}</span>
                  )}
                </NavLink>
              ))}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
